import { useEffect, useRef } from 'react';

interface Options {
  /** Stop observing once there is nothing left to load. */
  enabled?: boolean;
  /** How far ahead of the sentinel to fire, so the next page is already in. */
  rootMargin?: string;
  /**
   * A value that changes whenever the list grows — pass `items.length`.
   *
   * IntersectionObserver reports *transitions* only. On a tall screen the
   * sentinel can still be visible after a page lands, so without re-observing
   * per page nothing crosses the boundary again and the list stops one page in.
   */
  watch?: unknown;
}

/**
 * Ref for an element at the end of a list: `onReachEnd` fires as it scrolls
 * into view, so pages load on scroll rather than on a button press.
 *
 * `SInfiniteScroll` does this for the bookings list, but it owns its own
 * flex-column container and scroll element, which cannot render the shop's
 * two-column grid — hence the bare sentinel.
 *
 * `root` is left as the viewport deliberately: the shop screens scroll inside
 * their own containers, and the observer accounts for ancestor clipping, so
 * viewport intersection is the correct test either way.
 */
export function useInfiniteScroll<T extends HTMLElement = HTMLDivElement>(
  onReachEnd: () => void,
  { enabled = true, rootMargin = '400px', watch }: Options = {}
) {
  const ref = useRef<T | null>(null);
  // Read through a ref so a caller passing a fresh closure each render does not
  // tear down and rebuild the observer on every render.
  const handlerRef = useRef(onReachEnd);
  handlerRef.current = onReachEnd;

  useEffect(() => {
    const node = ref.current;
    if (!node || !enabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) handlerRef.current();
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, rootMargin, watch]);

  return ref;
}
